import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlightOffer } from "../../types/flight";
import DatePriceGrid from "./DatePriceGrid";
import FareDisclaimer from "./FareDisclaimer";
import FiltersBar from "./FiltersBar";
import FlightOfferGroup from "./FlightOfferGroup";

interface FlightResultsProps {
    flights: FlightOffer[];
    tripType: "one-way" | "round-trip" | "multi-city";
    searchDate: string;
    origin: string;
    destination: string;
    returnDate?: string;
    dictionaries?: Record<string, any>;
}

const FlightResults: React.FC<FlightResultsProps> = ({
    flights,
    tripType,
    searchDate,
    origin,
    destination,
    returnDate,
    dictionaries,
}) => {
    const navigate = useNavigate();
    const [selectedDate, setSelectedDate] = useState(searchDate);
    const [isViewingReturn, setIsViewingReturn] = useState(false);

    // Handle flight selection with fare brand details
    const handleSelectFlight = (
        flight: FlightOffer,
        fareType: "economy" | "premium" | "business",
        fareBrand?: string,
        price?: number
    ) => {
        const flightData = {
            flight,
            fareType,
            fareBrand: fareBrand || fareType,
            price: price || parseFloat(flight.price.total),
        };

        // Save to sessionStorage for persistence
        const selectionData = {
            ...flightData,
            tripType,
            dictionaries,
        };
        sessionStorage.setItem(
            "selectedFlightOffer",
            JSON.stringify(selectionData)
        );

        // Navigate to shopping cart page
        navigate("/philippine-airlines/shopping-cart");
    };

    // Get lowest fare for the date grid
    const getLowestFareForDate = (date: string): number | null => {
        // For round-trip, check the appropriate itinerary
        const itineraryIndex = isViewingReturn ? 1 : 0;

        const dayFlights = flights.filter((f) => {
            // Make sure the flight has the required itinerary
            if (!f.itineraries[itineraryIndex]) return false;

            const departureDate =
                f.itineraries[itineraryIndex].segments[0].departure.at.split(
                    "T"
                )[0];
            return departureDate === date;
        });

        if (dayFlights.length === 0) return null;
        const lowestPrice = Math.min(
            ...dayFlights.map((f) => parseFloat(f.price.total))
        );
        return lowestPrice;
    };

    // Note: Summary is now handled on a separate page via navigation

    // For round-trip, toggle between viewing departing and returning flights
    const handleDateChange = (newDate: string) => {
        setSelectedDate(newDate);

        // Check if we're switching between outbound and return dates
        if (tripType === "round-trip" && returnDate) {
            if (
                newDate === returnDate ||
                (isViewingReturn && newDate !== returnDate)
            ) {
                setIsViewingReturn(newDate === returnDate);
            }
        }
    };

    // Determine which itinerary to display
    const itineraryIndex = isViewingReturn ? 1 : 0;

    // Filter flights based on selected date and itinerary
    const filteredFlights = flights.filter((flight) => {
        // For round-trip, make sure the flight has both itineraries
        if (
            tripType === "round-trip" &&
            (!flight.itineraries[0] || !flight.itineraries[1])
        ) {
            return false;
        }

        // Check the appropriate itinerary
        const relevantItinerary = flight.itineraries[itineraryIndex];
        if (!relevantItinerary) return false;

        const departureDate =
            relevantItinerary.segments[0].departure.at.split("T")[0];
        return departureDate === selectedDate;
    });

    // Create modified flights for display when viewing return itinerary
    const displayFlights = isViewingReturn
        ? filteredFlights.map((flight) => ({
              ...flight,
              // Override the first itinerary with the second for display purposes
              itineraries: [
                  flight.itineraries[1],
                  ...flight.itineraries.slice(1),
              ],
          }))
        : filteredFlights;

    // Show appropriate origin/destination based on viewing phase
    const displayOrigin = isViewingReturn ? destination : origin;
    const displayDestination = isViewingReturn ? origin : destination;

    return (
        <div className="bg-[#e6edf8]" style={{ marginTop: "-110px" }}>
            {/* Date Price Grid */}
            <DatePriceGrid
                selectedDate={selectedDate}
                getLowestFare={getLowestFareForDate}
                origin={displayOrigin}
                destination={displayDestination}
                returnDate={isViewingReturn ? undefined : returnDate}
            />

            {/* Fare Disclaimer */}
            <FareDisclaimer />

            {/* Filters Bar */}
            <FiltersBar />

            {/* Flight Offer Groups */}
            <div className="py-2">
                {displayFlights.length > 0 ? (
                    <FlightOfferGroup
                        flights={displayFlights}
                        onSelectFlight={handleSelectFlight}
                    />
                ) : (
                    <div className="max-w-[1200px] mx-auto">
                        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                            <p className="text-gray-600">
                                No flights available for this date
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FlightResults;
